import EventHandler from '../../utils/dom/event-handler';
import Data from '../../utils/dom/data';
import { getElement } from '../../utils/utils';

/**
 * Base component class. Provides basic interface to store, retreive and dispose a component instance.
 *
 * @class BaseComponent
 */
class BaseComponent {
	/**
	 * Creates an instance of BaseComponent.
	 *
	 * @param {HTMLElement} element - Component DOM element
	 * @memberof BaseComponent
	 */
	constructor(element) {
		element = getElement(element);

		if (!element) {
			return;
		}

		this._element = element;
		Data.setData(this._element, this.constructor.DATA_KEY, this);
	}

	/**
	 * Dispose of the component instance. Will remove the component instance from the component data store.
	 *
	 * @memberof BaseComponent
	 */
	dispose() {
		Data.removeData(this._element, this.constructor.DATA_KEY);
		EventHandler.off(this._element, this.constructor.EVENT_KEY);

		for (const propertyName of Object.getOwnPropertyNames(this)) {
			// eslint-disable-next-line unicorn/no-null
			this[propertyName] = null;
		}
	}

	/**
	 * Get an instance of a component from an element
	 *
	 * @static
	 * @param {HTMLElement} element - DOM Element
	 * @returns {object} - Component instance for the passed DOM element
	 * @memberof BaseComponent
	 */
	static getInstance(element) {
		return Data.getData(element, this.DATA_KEY);
	}

	/**
	 * Get or create if missing an instance of a component from an element
	 *
	 * @param {HTMLElement} element - DOM Element
	 * @param {object} config - Config object
	 * @returns {object} - Component instance for the passed DOM element
	 * @memberof BaseComponent
	 */
	static getOrCreateInstance(element, config = {}) {
		// eslint-disable-next-line unicorn/no-null
		return this.getInstance(element) || new this(element, typeof config === 'object' ? config : null);
	}

	static get NAME() {
		throw new Error('You have to implement the static method "NAME" for each component!');
	}

	static get DATA_KEY() {
		return `lta.${this.NAME}`;
	}

	static get EVENT_KEY() {
		return `.${this.DATA_KEY}`;
	}
}

export default BaseComponent;
